import type { LibellesPartiels } from "@dozka/ui";

/**
 * Les mots que la librairie écrit d'elle-même — « Fermer », « Annuler », « 1–12 sur 205 »,
 * « Tout cocher sur la page », « Nouveau »… — dits dans la langue de l'app. La librairie est
 * en français sans rien et ne dépend d'aucun i18n (`CLAUDE.md`, « Les mots de la librairie ») :
 * l'app traduit avec le sien et pose le résultat UNE fois, à sa racine (`AppProviders`).
 * Sans cette enveloppe, un chatteur anglophone lit « Fermer » sur la croix d'une fenêtre.
 *
 *   - ce que l'app dit déjà vient de i18next : `common.close`, `common.confirm`,
 *     `common.cancel`, `common.yes`, `common.no` (aucun mot en double) ;
 *   - ce que l'app n'avait pas besoin de dire (le pied d'une liste, le `Tableau`, le `Delta`)
 *     est la table `MOTS` ci-dessous, en / es / it ; le français est le défaut de la librairie.
 *
 * Une prop explicite d'un composant (`closeLabel`, `confirmer`, `vide`…) l'emporte toujours.
 */

type Mots = Pick<LibellesPartiels, "pagination" | "tableau" | "delta">;

const MOTS: Record<"en" | "es" | "it", Mots> = {
  en: {
    pagination: { nom: "Pages", precedent: "Previous page", suivant: "Next page", sur: "of" },
    tableau: {
      toutCocher: "Select all on this page",
      cocher: (nom) => `Select ${nom}`,
      selectionnes: () => "selected",
      toutDecocher: "Deselect all",
      filtre: "Filter",
      filtres: "Filters",
      total: "Total",
      vide: "Nothing to show",
      detail: "Details",
    },
    delta: { nouveau: "New", rienAvant: "Nothing in the previous period", vs: "vs the previous period" },
  },
  es: {
    pagination: { nom: "Páginas", precedent: "Página anterior", suivant: "Página siguiente", sur: "de" },
    tableau: {
      toutCocher: "Seleccionar todo en la página",
      cocher: (nom) => `Seleccionar ${nom}`,
      selectionnes: (n) => (n > 1 ? "seleccionados" : "seleccionado"),
      toutDecocher: "Deseleccionar todo",
      filtre: "Filtro",
      filtres: "Filtros",
      total: "Total",
      vide: "Nada que mostrar",
      detail: "Detalle",
    },
    delta: { nouveau: "Nuevo", rienAvant: "Nada en el período anterior", vs: "vs el período anterior" },
  },
  it: {
    pagination: { nom: "Pagine", precedent: "Pagina precedente", suivant: "Pagina successiva", sur: "di" },
    tableau: {
      toutCocher: "Seleziona tutto nella pagina",
      cocher: (nom) => `Seleziona ${nom}`,
      selectionnes: (n) => (n > 1 ? "selezionati" : "selezionato"),
      toutDecocher: "Deseleziona tutto",
      filtre: "Filtro",
      filtres: "Filtri",
      total: "Totale",
      vide: "Niente da mostrare",
      detail: "Dettaglio",
    },
    delta: { nouveau: "Nuovo", rienAvant: "Niente nel periodo precedente", vs: "vs il periodo precedente" },
  },
};

/** Les `valeurs` de `<Libelles>` pour une langue de l'app (`fr` : le défaut de la librairie). */
export function libellesDeLApp(langue: string, t: (cle: string) => string): LibellesPartiels {
  const mots = langue in MOTS ? MOTS[langue as keyof typeof MOTS] : undefined;
  return {
    fermer: t("common.close"),
    confirmer: t("common.confirm"),
    annuler: t("common.cancel"),
    pagination: mots?.pagination,
    tableau: { ...mots?.tableau, oui: t("common.yes"), non: t("common.no") },
    delta: mots?.delta,
  };
}
